import type { ReactNode } from "react";
import { useLayoutEffect, useRef } from "react";

/**
 * Props accepted by {@link Collapsible}.
 */
interface CollapsibleProps {
  /** Whether the content is expanded (visible) or collapsed (hidden). */
  open?: boolean;
  /** Content to be shown/hidden inside the collapsible container. */
  children?: ReactNode;
}

/**
 * A container whose height animates between `0` and the natural height
 * of its content depending on the `open` prop.
 *
 * The transition duration is derived from the content height
 * (clamped between `0.25s` and `1s`) so that taller content expands
 * slightly slower than shorter content.
 */
export function Collapsible({ open, children }: CollapsibleProps): ReactNode {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const inner = innerRef.current;
    const outer = outerRef.current;

    if (inner && outer) {
      outer.style.maxHeight = open ? inner.scrollHeight + "px" : "0";

      let speed = inner.scrollHeight / 600;
      if (speed < 0.25) {
        speed = 0.25;
      }
      if (speed > 1) {
        speed = 1;
      }

      outer.style.transition = "all " + speed + "s";
    }
  }, [open, children]);

  return (
    <div ref={outerRef} className="collapsible" style={{ maxHeight: 0, overflow: "hidden", transition: "all 0.5s" }}>
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
